import { action, computed, makeAutoObservable } from 'mobx';
import { FilterValueType } from 'src/components/CmsCasinoManagement/SearchAndFilters/FilterDropdown';
import {
    CategoriesAndGamesCollection,
    GameStore,
} from 'src/domains/casino/state/AppCasino/store/game-store/Game.store';
import { CollectionType, GameType } from 'src/domains/casino/models/models';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { TreeType } from 'src/domains/casino/webview/bonus-engine/campaign/action-conditions/sportsbook-sections/sports-picker/utils';
import { sortByName } from 'src/domains/casino/utils/utils';
import { FormHelper, FormHelperFormType } from 'src/domains/casino/webview/bonus-engine/campaign/FormHelper';
import { casinoProviderUtils } from 'src/domains/casino/utils/CasinoProviderUtils';

export class GamePickerState {
    private readOnlyMode: boolean = false;
    public readonly collectionData: CategoriesAndGamesCollection;
    public showAllBadges: boolean = false;
    public checked: string[] = [];
    public expanded: string[] = [];
    public keywordInputState: FormInputState<string, unknown> = FormInputState.new('');
    private form: FormHelperFormType;

    public constructor(
        private readonly gameStore: GameStore,
        public readonly collection: CollectionType,
        public readonly pickerFormInputState?: FormInputState<string, string>
    ) {
        this.form = FormHelper.form;

        this.collectionData = this.gameStore.getCollection(this.collection);
        makeAutoObservable(this);
    }

    @computed public get allPragmaticGames(): TreeType[] {
        const pragmaticgames = this.collectionData.games.filter(
            (game) => casinoProviderUtils.convertNameToSlug(game.provider) === 'pragmatic' && game.published
        );

        return sortByName(pragmaticgames).map((gameValue) => {
            return {
                id: gameValue.id.toString(),
                value: gameValue.launch_game_id.toString(),
                label: gameValue.name,
                showCheckbox: !this.readOnlyMode,
            };
        });
    }

    @computed.struct public get games(): TreeType[] {
        return [
            {
                id: 'pragmatic',
                value: 'provider-pragmatic',
                label: 'Pragmatic',
                showCheckbox: !this.readOnlyMode,
                children: [
                    {
                        id: 'pragmatic-studio',
                        value: 'studio-pragmatic',
                        label: 'Pragmatic',
                        showCheckbox: !this.readOnlyMode,
                        children: this.allPragmaticGames,
                    },
                ],
            },
        ];
    }

    @action public checkIfGameMatchAllFilters = (
        isPublishedFilter: boolean,
        selectedStudioId: string,
        game: GameType
    ): boolean => {
        return (
            this.checkIfGameMatchPublishedFilter(isPublishedFilter, game) &&
            this.checkIfGameMatchStudioFilter(selectedStudioId, game)
        );
    };

    @action private checkIfGameMatchPublishedFilter = (isPublishedFilter: boolean, game: GameType): boolean => {
        return game.published === isPublishedFilter;
    };

    @action private checkIfGameMatchStudioFilter = (selectedStudioId: FilterValueType, game: GameType): boolean => {
        return selectedStudioId === game.studio_id || selectedStudioId === null;
    };

    @computed public get filteredNodes(): TreeType[] {
        const keyword = this.keyword.toLowerCase();
        if (keyword.trim() === '') return this.games;

        return this.filterNodes(this.games, keyword);
    }

    private filterNodes = (nodes: TreeType[], keyword: string): TreeType[] => {
        const filteredNodes: TreeType[] = [];
        for (const node of nodes) {
            if (node.label.toLowerCase().includes(keyword)) {
                filteredNodes.push({ ...node });
            } else if (node.children !== undefined) {
                const filteredChildren = this.filterNodes(node.children, keyword);
                if (filteredChildren.length > 0) {
                    filteredNodes.push({ ...node, children: filteredChildren });
                }
            }
        }
        return filteredNodes;
    };

    public handleCheck = (checked: string[]): void => {
        this.setChecked(checked);
        this.setIsTouched();
    };

    public handleExpand = (expanded: string[]): void => {
        this.expanded = expanded;
    };

    public setChecked = (checked: string[]): void => {
        this.checked = checked;
        this.pickerFormInputState?.setAsVisited();
        this.pickerFormInputState?.setValue(JSON.stringify(checked));
    };

    private setIsTouched(): void {
        if (this.isTouched() === false) {
            FormHelper.setIsTouched(this.form);
        }
    }

    public isTouched(): boolean {
        return this.form.isTouched();
    }

    public resetIsTouched(): void {
        if (this.isTouched()) {
            FormHelper.resetIsTouched(this.form);
        }
    }

    public expandToKeyword = (keyword: string): void => {
        const keywordLower = keyword.toLowerCase();
        const expandedKeys: string[] = [];

        const expandMatchingNodes = (nodes: TreeType[], parentKeys: string[] = []): void => {
            for (const node of nodes) {
                const currentKeys = [...parentKeys, node.value];
                if (node.label.toLowerCase().includes(keywordLower)) {
                    expandedKeys.push(...currentKeys);
                }
                if (node.children !== undefined) {
                    expandMatchingNodes(node.children, currentKeys);
                }
            }
        };

        expandMatchingNodes(this.games);
        this.handleExpand([...new Set(expandedKeys)]);
    };

    public expandAllNodes = (): void => {
        const expandedKeys: string[] = [];

        const expandMatchingNodes = (nodes: TreeType[], parentKeys: string[] = []): void => {
            for (const node of nodes) {
                const currentKeys = [...parentKeys, node.value];

                if (node.children !== undefined) {
                    expandedKeys.push(...currentKeys);
                    expandMatchingNodes(node.children, currentKeys);
                }
            }
        };

        expandMatchingNodes(this.games);
        this.handleExpand([...new Set(expandedKeys)]);
    };

    public setKeyword = (keyword: string): void => {
        this.keywordInputState.setValue(keyword);
        this.expandToKeyword(keyword);
    };

    public isEmpty(): boolean {
        return this.checked.length === 0;
    }

    public clearChecked = (): void => {
        if (this.isEmpty() === false && this.isTouched() === false) {
            this.setIsTouched();
        }

        this.checked = [];
        this.showAllBadges = false;
        this.pickerFormInputState?.setValue('');
    };

    public get keyword(): string {
        return this.keywordInputState.value;
    }

    public get visibleChecked(): { display: string[]; remaining: number } {
        const maxVisible = 3;

        if (this.showAllBadges) {
            return { display: this.checked, remaining: 0 };
        }

        const visibleChecked = this.checked.slice(0, maxVisible);
        const remaining = Math.max(0, this.checked.length - maxVisible);
        return { display: visibleChecked, remaining };
    }

    public handleRemoveChecked = (value: string): void => {
        this.setIsTouched();
        this.setChecked(this.checked.filter((item) => item !== value));
    };

    public toggleShowAllBadges = (): void => {
        this.showAllBadges = !this.showAllBadges;
    };

    public get visibleCheckedWithLabels(): { value: string; label: string }[] {
        const maxVisible = 3;
        const checkedToDisplay = this.showAllBadges ? this.checked : this.checked.slice(0, maxVisible);

        const getLabelForGame = (value: string): string => {
            const game = this.games
                .flatMap((provider) => {
                    if (provider.children === undefined) return [];
                    return provider.children.flatMap((studio) => {
                        if (studio.children === undefined) return [];
                        return studio.children;
                    });
                })
                .find((game) => game.value === value);

            if (game !== undefined) {
                return game.label;
            }

            return '';
        };

        return checkedToDisplay.map((value) => ({
            value,
            label: getLabelForGame(value),
        }));
    }

    public setReadOnlyMode = (value: boolean): void => {
        this.readOnlyMode = value;
    };
}
